import { useExperimentManager } from 'services/ExperimentManager/ExperimentManager'
import { MAX_PRICED_BOOK_PAGES } from 'services/PhotoBook/bookPageTier'
import { PRODUCT_TYPES } from 'services/ProductTypeState'
import { useCartStore } from 'stores/cartStore/CartStoreProvider'
import type {
  AiTileGeneratingCartItemWarning,
  CartItem,
  IncompleteCollageCartItemWarning,
  NotEnoughPhotosCartItemWarning,
  PhotoBookCartItem,
  TilesCartItem,
} from 'stores/cartStore/CartItem'
import { useMobxSelector } from 'utils/mobx/useMobxSelector'
import { isBlockingCartWarning } from '../cartWarning.config'

// `type` sits on the blocker itself so switching on it narrows `item` too.
export type CheckoutBlocker =
  | {
      type: 'incomplete-collage'
      item: TilesCartItem
      warning: IncompleteCollageCartItemWarning
    }
  | {
      type: 'ai-tile-generating'
      item: TilesCartItem
      warning: AiTileGeneratingCartItemWarning
    }
  | {
      type: 'not-enough-photos'
      item: PhotoBookCartItem
      warning: NotEnoughPhotosCartItemWarning
    }
  | {
      type: 'too-many-pages'
      item: PhotoBookCartItem
      warning: undefined
    }

function getCartItemCheckoutBlocker(
  item: CartItem,
  isPriceTierEnabled: boolean
): CheckoutBlocker | null {
  switch (item.productType) {
    case PRODUCT_TYPES.CLASSIC: {
      const warning = item.warnings?.find(isBlockingCartWarning)
      if (warning?.type === 'incomplete-collage') {
        return { type: warning.type, item, warning }
      }
      if (warning?.type === 'ai-tile-generating') {
        return { type: warning.type, item, warning }
      }
      return null
    }
    case PRODUCT_TYPES.PHOTO_BOOK: {
      // Page tiers end at MAX_PRICED_BOOK_PAGES; without tiers a book is
      // priced by size alone and the backend takes any page count.
      if (
        isPriceTierEnabled &&
        item.metadata.photosCount > MAX_PRICED_BOOK_PAGES
      ) {
        return { type: 'too-many-pages', item, warning: undefined }
      }
      const warning = item.warnings?.find(isBlockingCartWarning)
      return warning ? { type: warning.type, item, warning } : null
    }
  }
}

function findCheckoutBlocker(
  items: CartItem[],
  isPriceTierEnabled: boolean
): CheckoutBlocker | null {
  for (const item of items) {
    const checkoutBlocker = getCartItemCheckoutBlocker(item, isPriceTierEnabled)
    if (checkoutBlocker) return checkoutBlocker
  }
  return null
}

/**
 * The one blocker checkout stops on: cart order picks it, so several blockers
 * are prompted one at a time — fixing the prompted one promotes the next into
 * the next checkout attempt.
 */
export function useCheckoutBlocker(): CheckoutBlocker | null {
  const cartStore = useCartStore()
  const experimentManager = useExperimentManager()
  const isPriceTierEnabled = experimentManager.isEnabled(
    'pricing_photo_book_price_tier'
  )
  return useMobxSelector(
    () => findCheckoutBlocker(cartStore.cartItems, isPriceTierEnabled),
    [cartStore, isPriceTierEnabled]
  )
}
